import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type BadgeProps = {
  children: ReactNode;
  variant?: "solid" | "subtle" | "outline";
  color?: "brand" | "gray" | "green" | "red" | "yellow" | "blue" | "purple";
  size?: "sm" | "md";
  className?: string;
};

const sizeStyles = {
  sm: "px-1.5 py-0.5 text-xs",
  md: "px-2 py-0.5 text-sm",
};

const colorStyles = {
  solid: {
    brand: "bg-brand-500 text-surface-900",
    gray: "bg-surface-600 text-white",
    green: "bg-green-500 text-surface-900",
    red: "bg-red-500 text-white",
    yellow: "bg-yellow-500 text-surface-900",
    blue: "bg-blue-500 text-white",
    purple: "bg-purple-500 text-white",
  },
  subtle: {
    brand: "bg-brand-500/20 text-brand-400",
    gray: "bg-surface-700 text-white",
    green: "bg-green-500/20 text-green-400",
    red: "bg-red-500/20 text-red-400",
    yellow: "bg-yellow-500/20 text-yellow-400",
    blue: "bg-blue-500/20 text-blue-400",
    purple: "bg-purple-500/20 text-purple-400",
  },
  outline: {
    brand: "border border-brand-500 text-brand-400",
    gray: "border border-surface-600 text-surface-300",
    green: "border border-green-500 text-green-400",
    red: "border border-red-500 text-red-400",
    yellow: "border border-yellow-500 text-yellow-400",
    blue: "border border-blue-500 text-blue-400",
    purple: "border border-purple-500 text-purple-400",
  },
};

export function Badge({
  children,
  variant = "subtle",
  color = "gray",
  size = "sm",
  className = "",
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium",
        sizeStyles[size],
        colorStyles[variant][color],
        className,
      )}
    >
      {children}
    </span>
  );
}
